'use client';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { HighlightOff } from '@mui/icons-material';
import {
    formHelperTextClasses,
    IconButton,
    InputBaseComponentProps,
    TextField,
    TextFieldProps
} from '@mui/material';
import { FocusEvent, useState } from 'react';
import { useTranslations } from 'next-intl';

type Props = Omit<TextFieldProps, 'onError' | 'error' | 'helperText'> & {
    maxLength?: number;
    onClear?: () => void;
    isValid?: boolean;
    /* Text shown under the input field. Will be shown unless there is an errorText defined and the field is in error state */
    helperText?: string;
    /* Will only be shown when the field is dirty and isValid is false */
    errorText?: string;
    errorEvaluation?: 'invalid' | 'dirtyAndInvalid' | 'dirtyAndInvalidAndNotEmpty';
    inputProps?: InputBaseComponentProps;
};

const DHTextField = ({
    maxLength,
    onChange,
    onClear,
    isValid = true,
    inputProps,
    errorText,
    errorEvaluation = 'dirtyAndInvalidAndNotEmpty',
    helperText,
    disabled,
    required,
    ...props
}: Props) => {
    const t_forms = useTranslations('forms');
    const { colors } = useTheme();

    const [isDirty, setIsDirty] = useState(false);

    // Defaults to 0 for non string types, as we consider chars only for strings.
    const charCount = typeof props.value === 'string' ? props.value.length : 0;
    // Check if we have a value. For string type the char count is used for other types we check for undefined
    const isEmpty = typeof props.value === 'string' ? charCount === 0 : props.value === undefined;

    // errorEvaluation (which is by default 'dirtyAndInvalidAndNotEmpty') decides when to show an error state.
    // Normally we want to show an error only, when it is changed, invalid and not empty.
    // But in some cases we want to show an error as soon as it is invalid, e.g. for mandatory fields.
    const inErrorState =
        errorEvaluation === 'invalid'
            ? !isValid
            : errorEvaluation == 'dirtyAndInvalid'
              ? isDirty && !isValid
              : isDirty && !isValid && !isEmpty;

    const externalHelperText = inErrorState && errorText ? errorText : helperText;

    const requiredPrefix = required ? t_forms('mandatory_input') : '';
    const label = props.label + requiredPrefix;
    const placeholder = props.placeholder ? props.placeholder + requiredPrefix : label;

    const onBlur = (event: FocusEvent<HTMLInputElement, Element>) => {
        props.onBlur && props.onBlur(event);

        if (event.target.value.length > 0) setIsDirty(true);
    };

    return (
        <StyledTextField
            disabled={disabled}
            helperText={
                externalHelperText ??
                (maxLength
                    ? t_forms('characters', {
                          count: charCount,
                          allowed: maxLength
                      })
                    : undefined)
            }
            error={inErrorState}
            onChange={onChange}
            required={false}
            sx={{ fieldset: { borderColor: colors.onSurfaceVariant } }}
            slotProps={{
                input: {
                    style: {
                        minHeight: 56,
                        flex: 1,
                        backgroundColor: colors.surface
                    },
                    endAdornment: onClear ? (
                        <IconButton aria-label='clear input' onClick={onClear}>
                            <HighlightOff color='inherit' />
                        </IconButton>
                    ) : undefined,

                    ...props.slotProps?.input
                },
                inputLabel: { sx: { color: colors.onSurfaceVariant } },
                htmlInput: {
                    maxLength: maxLength,
                    ...inputProps
                }
            }}
            {...props}
            onBlur={onBlur}
            label={label}
            placeholder={placeholder}
        />
    );
};

export const StyledTextField = styled(TextField)(({ theme, helperText, disabled, error }) => ({
    borderColor: theme.colors.onSurface,
    [`.${formHelperTextClasses.disabled}`]: {
        color: theme.colors.disabledText
    },
    '& .MuiInputBase-input.Mui-disabled': {
        color: `${theme.colors.disabledText} !important`,
        WebkitTextFillColor: `${theme.colors.disabledText} !important`
    },
    [`& .${formHelperTextClasses.root}`]: {
        color: disabled
            ? theme.colors.disabledText
            : error
              ? theme.colors.error
              : helperText
                ? theme.colors.onSurface
                : theme.colors.outline
    }
}));

export { DHTextField };
